import { useCallback, useRef } from 'react'
import { Col, Row } from 'react-grid-system'
import { NormalFormDateField, NormalFormNumberField, NormalFormTextField } from '../molecules'
import { useUserLanguage } from '@/customHooks'
import { CreateIncidentExpenseInput } from '@/graphql/generated'
import textsData from '@/textData/ExpenseFormTexts.json'

type AddIncidentExpenseFormControlledProps = {
  onChange: (data: CreateIncidentExpenseInput) => void
  defaultData?: CreateIncidentExpenseInput
}

const emptyData: CreateIncidentExpenseInput = {
  amount: '',
  description: '',
  invoiceDate: '',
  invoiceNumber: '',
}

export const AddIncidentExpenseFormControlled = ({ onChange, defaultData }: AddIncidentExpenseFormControlledProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.form : textsData.en.form

  const expenseDataRef = useRef<CreateIncidentExpenseInput>(defaultData || emptyData)

  const onChangeHandle = useCallback(
    (field: keyof CreateIncidentExpenseInput, value: string | Date | null | number) => {
      let processedValue: string | null | Date | number = value

      if (field === 'invoiceDate') {
        if (value instanceof Date && !isNaN(value.getTime())) {
          processedValue = value.toISOString()
        } else if (typeof value === 'string' && value.trim() !== '') {
          const parsedDate = new Date(value)
          if (!isNaN(parsedDate.getTime())) {
            processedValue = parsedDate.toISOString()
          } else {
            processedValue = ''
          }
        } else {
          processedValue = ''
        }
      }

      if (field === 'amount') {
        processedValue = value ? String(value) : ''
      }

      expenseDataRef.current = {
        ...expenseDataRef.current,
        [field]: processedValue as any,
      }
      onChange(expenseDataRef.current)
    },
    [onChange],
  )

  return (
    <>
      <Row>
        <Col>
          <NormalFormDateField
            label={texts.date.label}
            name="invoiceDate"
            onChange={(value) => onChangeHandle('invoiceDate', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.invoiceNumber.label}
            placeholder={texts.invoiceNumber.placeholder}
            name="invoiceNumber"
            onChange={(value) => onChangeHandle('invoiceNumber', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormNumberField
            label={texts.cost.label}
            name="amount"
            onChange={(value) => onChangeHandle('amount', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.description.label}
            placeholder={texts.description.placeholder}
            name="expenseDescription"
            onChange={(value) => onChangeHandle('description', value)}
            required
          />
        </Col>
      </Row>
    </>
  )
}
